import type { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import type { ISyncOperation } from "@/store/sync/operations/types";
import { CreateNoteOperation } from "@/store/sync/operations/notes/CreateNoteOperation";
import { AcceptNoteSuggestionOperation } from "@/store/sync/operations/notes/AcceptNoteSuggestionOperation";
import autoOrganizeImage from "@/assets/images/guide-card-auto-organize.gif";
import voiceModeImage from "@/assets/images/guide-card-voice-mode.gif";

/**
 * Type-safe step identifiers for getting started guide.
 * Using const assertion for compile-time type safety and IDE autocomplete.
 */
export const STEP_IDS = {
  CREATE_FIRST_NOTE: "create_first_note",
  SEND_CHAT_MESSAGE: "send_chat_message",
  ACCEPT_VOICE_CHANGES: "accept_voice_changes",
  AUTO_ORGANIZE_NOTE: "auto_organize_note",
} as const;

/**
 * Help content for steps that have contextual help
 */
export interface StepHelpContent {
  title: string;
  description: string;
  image: string;
}

/**
 * Configuration interface for getting started steps
 */
export interface StepConfig {
  id: StepId;
  title: string;
  /**
   * Optional tooltip text shown on hover
   */
  tooltip?: string;
  /**
   * Optional function to match sync operations for automatic completion detection.
   * If not provided, the step must be completed manually via markStepComplete().
   */
  matchesOperation?: (operation: BaseSyncOperation<ISyncOperation>) => boolean;
  /**
   * Optional help content shown in modal when user clicks the help icon
   */
  helpContent?: StepHelpContent;
}

/**
 * Runtime step data interface
 */
export interface Step {
  id: StepId;
  title: string;
  completedAt?: Date;
}

/**
 * Configuration for all getting started steps.
 * Steps are processed in order and displayed to users in this sequence.
 */
export const STEP_CONFIGS: StepConfig[] = [
  {
    id: STEP_IDS.CREATE_FIRST_NOTE,
    title: "Create a note",
    tooltip: "Completes when you create your first note",
    matchesOperation: operation => operation instanceof CreateNoteOperation,
  },
  {
    id: STEP_IDS.AUTO_ORGANIZE_NOTE,
    title: "Auto Organize a note",
    tooltip: "Completes when you use Auto-Organize",
    // No matchesOperation - manually triggered from autoOrganizeNotes action
    helpContent: {
      title: "How to Auto Organize a note",
      description:
        'Mem will suggest a collection and organize your note for you automatically. Click "Organize" from any note and select "Auto organize" to try it yourself.',
      image: autoOrganizeImage,
    },
  },
  {
    id: STEP_IDS.SEND_CHAT_MESSAGE,
    title: "Ask Chat about your note",
    tooltip: "Completes when you use Chat",
    // No matchesOperation - manually triggered from MemItPageStore
  },
  {
    id: STEP_IDS.ACCEPT_VOICE_CHANGES,
    title: "Take a note with Voice Mode",
    tooltip: "Completes when you accept a note created from Voice Mode",
    matchesOperation: operation => operation instanceof AcceptNoteSuggestionOperation,
    helpContent: {
      title: "How to take a note with Voice Mode",
      description:
        "Record a meeting, or brain dump your tasks this week. Create a note and simply start talking—Mem will automatically transcribe and create organized notes for you.",
      image: voiceModeImage,
    },
  },
];

/**
 * Union type of all valid step IDs.
 * This provides type safety when passing step IDs to methods like markStepComplete().
 */
export type StepId = (typeof STEP_IDS)[keyof typeof STEP_IDS];
